import React, { isValidElement, type ReactElement } from 'react';
import type { FiltersGroupId } from '@/app/common/filters/core/filtersTypes.ts';
import { CUSTOM_GROUP_NAME, FILTER_NAME } from '@/app/common/filters/core/filterNames.ts';
import { QUICK_FILTER } from '@/app/common/filters/quickFilters/quickFilterCatalogue.ts';
import { getMealPlanEvoIcon } from '@/app/components/search/components/Filters/verticalFilters/Fields/getMealPlanField/getMealPlanIcon.tsx';
import { MaskIcon } from '@/app/common/icons/MaskIcon';

const PILL_ICON_SIZE = 20;

export const ICON_ALL_FILTERS = <MaskIcon category="UI Essentials" name="icon-filter" size={PILL_ICON_SIZE} />;

export const ICON_SORT = <MaskIcon category="UI Essentials" name="icon-sort" size={PILL_ICON_SIZE} />;

export const ICON_ACCESSIBILITY = (
  <MaskIcon category="People and users" name="icon-accessibility" size={PILL_ICON_SIZE} />
);

export const ICON_CASH = <MaskIcon category="Financial" name="icon-cash" size={PILL_ICON_SIZE} />;

export const ICON_CHILD = <MaskIcon category="Services" name="icon-child" size={PILL_ICON_SIZE} />;

export const ICON_CUTLERY = <MaskIcon category="Food and beverages" name="icon-cutlery" size={PILL_ICON_SIZE} />;

export const ICON_HEART_BUBBLE = (
  <MaskIcon category="Rating and feedback" name="icon-heart-bubble" size={PILL_ICON_SIZE} />
);

export const ICON_PARKING = <MaskIcon category="Services" name="icon-parking" size={PILL_ICON_SIZE} />;

export const ICON_STAR = <MaskIcon category="Rating and feedback" name="icon-star" size={PILL_ICON_SIZE} />;

export const ICON_SWIMMING_POOL = <MaskIcon category="Services" name="icon-swimming-pool" size={PILL_ICON_SIZE} />;

export const ICON_WELLNESS = <MaskIcon category="Services" name="icon-wellness" size={PILL_ICON_SIZE} />;

export const ICON_WIFI = <MaskIcon category="Services" name="icon-wifi" size={PILL_ICON_SIZE} />;

const MEAL_PLAN = { BREAKFAST: '2', FULL_BOARD: '4', ALL_INCLUSIVE: '5' } as const;

/**
 * Shared with every other evo surface, so a pill cannot disagree with them. Read defensively: this
 * runs while both bars load the module, so anything missing comes back as a bare pill rather than a
 * broken bar.
 */
const mealPlanSource: unknown = getMealPlanEvoIcon();
const mealPlanIcons = (Array.isArray(mealPlanSource) ? mealPlanSource : []).map((icon: unknown) =>
  isValidElement(icon) ? icon : undefined
);

/** Value '0' is the default "any" option, which never reads as a selection. */
export const getMealPlanPillIcon = (value: string): ReactElement | undefined =>
  Number(value) > 0 ? mealPlanIcons[Number(value)] : undefined;

// Record demands an entry per quick filter, so a new one without an icon fails the build;
// pillIcons.test.ts is what holds the entries themselves to being real icons.
const quickFilterIcons: Record<QUICK_FILTER, ReactElement | undefined> = {
  [QUICK_FILTER.STARS_4_TO_5]: ICON_STAR,
  [QUICK_FILTER.RATING_4_PLUS]: ICON_HEART_BUBBLE,
  [QUICK_FILTER.BREAKFAST]: getMealPlanPillIcon(MEAL_PLAN.BREAKFAST),
  [QUICK_FILTER.FULL_BOARD]: getMealPlanPillIcon(MEAL_PLAN.FULL_BOARD),
  [QUICK_FILTER.ALL_INCLUSIVE]: getMealPlanPillIcon(MEAL_PLAN.ALL_INCLUSIVE),
  [QUICK_FILTER.HIGH_SPEED_INTERNET]: ICON_WIFI,
  [QUICK_FILTER.PARKING]: ICON_PARKING,
  [QUICK_FILTER.SWIMMING_POOL]: ICON_SWIMMING_POOL,
  [QUICK_FILTER.SPA]: ICON_WELLNESS,
  [QUICK_FILTER.ACCESSIBILITY]: ICON_ACCESSIBILITY,
  [QUICK_FILTER.KIDS_CLUB]: ICON_CHILD,
  [QUICK_FILTER.RESTAURANT_BAR]: ICON_CUTLERY,
};

export const getQuickFilterIcon = (id: string): ReactElement | undefined => quickFilterIcons[id as QUICK_FILTER];

const icons: Partial<Record<string, ReactElement>> = {
  [CUSTOM_GROUP_NAME.ALL_FILTERS]: ICON_ALL_FILTERS,
  [FILTER_NAME.SORT]: ICON_SORT,
  [FILTER_NAME.PRICE_RANGE]: ICON_CASH,
};

export const getPillIcon = (id: FiltersGroupId): ReactElement | undefined => icons[id];
